import React, { useCallback, useEffect, useMemo, useState } from "react";
import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import api, { extractApiError } from "@/lib/api";
import { Shield, Filter, ChevronDown, X, RefreshCw } from "lucide-react";

const TARGET_TYPE_OPTIONS = ["USER", "ORG", "VEHICLE", "DEVICE", "TRIP", "INCIDENT"];
const PAGE_SIZE_OPTIONS = [10, 20, 50];

const EVENT_NAME_OPTIONS = [
  "AUTHLOGOUT",
  "ONBOARDCOMPLETE",
  "ORGCREATE", "ORGUPDATE", "ORGDELETE",
  "USERCREATE", "USERUPDATE", "USERDELETE",
  "USERPERMISSIONSUPDATE", "USERPERMISSIONSRESET",
  "VEHICLECREATE", "VEHICLEUPDATE", "VEHICLEDELETE",
  "DEVICECREATE", "DEVICEDELETE", "DEVICESTATUSUPDATE",
  "INCIDENTSTATUSUPDATE", "INCIDENTSERVICERESPONSE",
  "TRIPCREATE", "TRIPUPDATE", "TRIPDELETE",
];

function toIsoDateTime(localDateTime) {
  if (!localDateTime) return undefined;
  const dt = new Date(localDateTime);
  if (Number.isNaN(dt.getTime())) return undefined;
  return dt.toISOString();
}

function formatDateTime(value) {
  if (!value) return "-";
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return "-";
  return dt.toLocaleString("en-IN", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function severityClass(severity) {
  if (severity === "ERROR" || severity === "CRITICAL") return "bg-rose-50 text-rose-700";
  if (severity === "WARN" || severity === "WARNING") return "bg-amber-50 text-amber-700";
  return "bg-blue-50 text-blue-700";
}

export default function SecurityLogsPage() {
  const [eventName, setEventName] = useState("");
  const [targetType, setTargetType] = useState("all");
  const [userId, setUserId] = useState("");
  const [fromDt, setFromDt] = useState("");
  const [toDt, setToDt] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const [logs, setLogs] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, page_size: 50, pages: 1 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const activeFiltersCount = useMemo(
    () => [!!eventName.trim(), targetType !== "all", !!userId.trim(), !!fromDt, !!toDt].filter(Boolean).length,
    [eventName, targetType, userId, fromDt, toDt],
  );

  const resetFilters = () => {
    setEventName("");
    setTargetType("all");
    setUserId("");
    setFromDt("");
    setToDt("");
    setPage(1);
  };

  const fetchAuditLogs = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {
        page,
        page_size: pageSize,
      };

      if (eventName.trim()) params.event_name = eventName.trim();
      if (targetType !== "all") params.target_type = targetType;
      if (userId.trim()) params.user_id = userId.trim();

      const fromIso = toIsoDateTime(fromDt);
      const toIso = toIsoDateTime(toDt);
      if (fromIso) params.from_dt = fromIso;
      if (toIso) params.to_dt = toIso;

      const { data } = await api.get("/audit-logs", { params });
      setLogs(Array.isArray(data?.data) ? data.data : []);
      setMeta(
        data?.meta || {
          total: 0,
          page,
          page_size: pageSize,
          pages: 1,
        },
      );
    } catch (err) {
      const { message } = extractApiError(err);
      setError(message);
      setLogs([]);
      setMeta({ total: 0, page, page_size: pageSize, pages: 1 });
    } finally {
      setLoading(false);
    }
  }, [eventName, targetType, userId, fromDt, toDt, page, pageSize]);

  useEffect(() => {
    fetchAuditLogs();
  }, [fetchAuditLogs]);

  return (
    <div className="space-y-6" data-testid="security-logs">
      <PageHeader title="Security Logs" subtitle="Audit trail across all admins, services and overrides." />

      <div className="flex flex-col gap-4 bg-white p-4 rounded-2xl border border-slate-100 shadow-sm">
        <div className="flex items-center gap-3">
          <Select
            value={eventName || "all"}
            onValueChange={(v) => {
              setEventName(v === "all" ? "" : v);
              setPage(1);
            }}
          >
            <SelectTrigger className="h-11 flex-1 border-slate-200 bg-slate-50/50">
              <SelectValue placeholder="All event types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All event types</SelectItem>
              {EVENT_NAME_OPTIONS.map((ev) => (
                <SelectItem key={ev} value={ev}>{ev}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="h-11 gap-2 border-slate-200 bg-white hover:bg-slate-50">
                <Filter size={14} className="text-slate-400" />
                <span>Filters</span>
                {activeFiltersCount > 0 && (
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-slate-900 text-[10px] font-bold text-white">
                    {activeFiltersCount}
                  </span>
                )}
                <ChevronDown size={14} className="text-slate-400" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[360px] p-4" align="end">
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-black uppercase tracking-widest text-slate-400">Target Type</label>
                  <Select
                    value={targetType}
                    onValueChange={(value) => {
                      setTargetType(value);
                      setPage(1);
                    }}
                  >
                    <SelectTrigger className="h-10 bg-slate-50/50 border-slate-200">
                      <SelectValue placeholder="All target types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      {TARGET_TYPE_OPTIONS.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <label className="text-[11px] font-black uppercase tracking-widest text-slate-400">User</label>
                  <Input
                    value={userId}
                    onChange={(e) => {
                      setUserId(e.target.value);
                      setPage(1);
                    }}
                    placeholder="Filter by user"
                    className="h-10 bg-slate-50/50 border-slate-200"
                  />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-black uppercase tracking-widest text-slate-400">From</label>
                    <Input
                      type="datetime-local"
                      value={fromDt}
                      onChange={(e) => {
                        setFromDt(e.target.value);
                        setPage(1);
                      }}
                      className="h-10 bg-slate-50/50 border-slate-200"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-black uppercase tracking-widest text-slate-400">To</label>
                    <Input
                      type="datetime-local"
                      value={toDt}
                      onChange={(e) => {
                        setToDt(e.target.value);
                        setPage(1);
                      }}
                      className="h-10 bg-slate-50/50 border-slate-200"
                    />
                  </div>
                </div>

                <Button variant="ghost" onClick={resetFilters} className="w-full h-10 text-slate-500 hover:bg-slate-50">
                  <X size={14} className="mr-1" /> Reset Filters
                </Button>
              </div>
            </PopoverContent>
          </Popover>

          <Select
            value={String(pageSize)}
            onValueChange={(value) => {
              setPageSize(Number(value));
              setPage(1);
            }}
          >
            <SelectTrigger className="h-11 border-slate-200 bg-white w-[110px]">
              <SelectValue placeholder="Size" />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button variant="outline" onClick={fetchAuditLogs} className="h-11 gap-2 border-slate-200 bg-white hover:bg-slate-50">
            <RefreshCw size={14} /> Refresh
          </Button>
        </div>

        <div className="flex items-center justify-between px-1">
          <span className="text-xs font-medium text-slate-500">
            {loading ? "Loading logs..." : `${meta.total} logs found`}
          </span>
          <span className="text-xs text-slate-500">
            Page {meta.page || page} of {meta.pages || 1}
          </span>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}

      <div className="new-fix-hei_two">
      <SectionCard padded={false}>
        <ul className="divide-y divide-slate-100">
          {logs.map((l) => (
            <li key={l.id} className="flex items-start gap-4 px-5 py-4">
              <div className="mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-700">
                <Shield size={15} strokeWidth={1.75} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-slate-900">{l.event_name || "UNKNOWN_EVENT"}</p>
                    <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-[10px] font-bold ${severityClass(l.severity)}`}>
                      {l.severity || "INFO"}
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-400">{formatDateTime(l.occurred_at)}</p>
                </div>
                <p className="mt-0.5 text-xs text-slate-500 break-all">
                  Target {l.target_type || "-"}
                  {/* {l.target_id ? `:${l.target_id}` : ""} */}
                  {" · "}HTTP {l.metadata?.http_status ?? "-"}
                  {" · "}IP {l.ip_address || "-"}
                </p>
                <p className="mt-0.5 text-[11px] text-slate-400 truncate">{l.user_agent || "-"}</p>
              </div>
            </li>
          ))}

          {!loading && logs.length === 0 && (
            <li className="px-5 py-10 text-center text-sm text-slate-500">No audit logs found for the selected filters.</li>
          )}
        </ul>

        <div className="flex items-center justify-end gap-2 border-t border-slate-100 p-4">
          <Button
            variant="outline"
            className="border-slate-200"
            disabled={loading || page <= 1}
            onClick={() => setPage((prev) => Math.max(1, prev - 1))}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            className="border-slate-200"
            disabled={loading || page >= (meta.pages || 1)}
            onClick={() => setPage((prev) => Math.min(meta.pages || 1, prev + 1))}
          >
            Next
          </Button>
        </div>
      </SectionCard>
      </div>


    </div>
  );
}
